import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { Card, Result } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { NotFound } from "../../../app/pages/NotFound";
import { useGetPartnerConfigsAndSsoProperties, useMonlyAuthorizationCodeGrant } from "../../../common/api/queries";
import { PartnerSsoType } from "../enums";
import { usePartnerSsoActions, usePartnerSsoState } from "../store";
import { redirectToMonlyAccessTokenLogin } from "../utils";

export const PartnerSsoOAuthClientContainer = () => {
  const { t } = useTranslation();

  const [urlParams] = useSearchParams();
  const code = urlParams.get("code");
  const state = urlParams.get("state");

  const partnerSsoState = usePartnerSsoState();
  const partnerSsoActions = usePartnerSsoActions();

  const monlyAuthorizationCodeGrant = useMonlyAuthorizationCodeGrant();

  // The state is single use and gets consumed as the grant starts, so from then on the grant marks the params found
  const ssoParamsState = !monlyAuthorizationCodeGrant.isIdle
    ? "found"
    : !code || !state || state !== partnerSsoState || state.split("_")[0] !== PartnerSsoType.MONLY
      ? "notFound"
      : "notInitialized";

  const { data: partnerConfigs, isError: isPartnerConfigsError } = useGetPartnerConfigsAndSsoProperties(
    ssoParamsState === "found",
  );

  const accessToken = monlyAuthorizationCodeGrant.data?.accessToken;
  const monlySsoBaseUrl = partnerConfigs?.monlySsoProperties?.baseUrl;

  // Unlike a failed request, a missing baseUrl gets no error notification.
  const isSsoFailed =
    monlyAuthorizationCodeGrant.isError || isPartnerConfigsError || (!!partnerConfigs && !monlySsoBaseUrl);

  useEffect(() => {
    if (!code || !state || state !== partnerSsoState || !partnerSsoActions.ssoCallbackReceived(state)) {
      return;
    }

    const [statePrefix] = state.split("_");
    if (statePrefix === PartnerSsoType.MONLY) {
      monlyAuthorizationCodeGrant.mutate(code);
    }
  }, [urlParams]);

  // Not in the grant's onSuccess: in this freshly opened tab the configs may arrive only after the grant.
  useEffect(() => {
    if (accessToken && monlySsoBaseUrl) {
      redirectToMonlyAccessTokenLogin(accessToken, "_self", monlySsoBaseUrl);
    }
  }, [accessToken, monlySsoBaseUrl]);

  return (
    <DisplayWrapper itemLoaded={ssoParamsState !== "notInitialized"}>
      {ssoParamsState === "notFound" ? (
        <NotFound />
      ) : (
        <ContentWrapper>
          <Card className="card-box" title={<h2>{t("partnerSso.titles.ssoPage")}</h2>}>
            <Result
              status={isSsoFailed ? "error" : "info"}
              icon={isSsoFailed ? undefined : <AntIcon type="loading" />}
              title={t(
                isSsoFailed
                  ? "partnerSso.titles.automaticAuthenticationFailed"
                  : "partnerSso.titles.automaticAuthentication",
              )}
            />
          </Card>
        </ContentWrapper>
      )}
    </DisplayWrapper>
  );
};
